import { Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider } from "./auth/AuthProvider";
import { RequireAuth } from "./auth/RequireAuth";
import { RequirePage } from "./auth/RequirePage";
import { Layout } from "./components/Layout";

/* Page-permission slug groups — mirror the NAV anyOf lists in lib/permissions. */
const CRAWLER_PAGES = [
  "Newspaper_CrawlerOps_TodayRuns", "Newspaper_CrawlerOps_Queue",
  "Newspaper_CrawlerOps_Attempts", "Newspaper_CrawlerOps_Alerts",
  "Newspaper_CrawlerOps_Heartbeat", "Newspaper_CrawlerOps_Audit",
];
const MASTERS_PAGES = [
  "Newspaper_Masters_PublisherGroup", "Newspaper_Masters_Editions",
  "Newspaper_Masters_CrawlerConfig", "Newspaper_Masters_AuthProfile",
  "Newspaper_Masters_Storage",
];
const USERS_PAGES = ["Newspaper_Users_List", "Newspaper_Users_Groups", "Newspaper_Users_Rights"];

import { Login } from "./pages/Login";
import { ChangePassword } from "./pages/ChangePassword";
import { Home } from "./pages/Home";
import { Browse } from "./pages/Browse";
import { Publisher } from "./pages/Publisher";
import { Edition } from "./pages/Edition";
import { Reader } from "./pages/Reader";
import { MastersEditions } from "./pages/MastersEditions";
import { CrawlerSessions } from "./pages/CrawlerSessions";
import { CrawlerPublishers } from "./pages/CrawlerPublishers";
import { CrawlerChannelMaster } from "./pages/CrawlerChannelMaster";
import { Users } from "./pages/Users";
import { UserSessions } from "./pages/UserSessions";
import { Stub } from "./pages/Stub";
import { CrawlerCoverage } from "./pages/CrawlerCoverage";
import { AgentConsole } from "./pages/AgentConsole";
import { AgentConsole3 } from "./pages/AgentConsole3";
import { RequireGroup } from "./auth/RequireGroup";
import { PrintSearch } from "./pages/PrintSearch";
import { PageResolution } from "./pages/PageResolution";

export function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/change-password" element={
          <RequireAuth><ChangePassword /></RequireAuth>
        } />
        <Route element={
          <RequireAuth><Layout /></RequireAuth>
        }>
          <Route index element={<Home />} />

          {/* Browse → publisher → edition → reader (Publishers nav) */}
          <Route path="/browse" element={<RequirePage anyOf={["Newspaper_Browse"]}><Browse /></RequirePage>} />
          <Route path="/browse/:channelId" element={<RequirePage anyOf={["Newspaper_Browse"]}><Publisher /></RequirePage>} />
          <Route path="/browse/:channelId/:editionId" element={<RequirePage anyOf={["Newspaper_Browse"]}><Edition /></RequirePage>} />
          <Route path="/reader/:issueId" element={<RequirePage anyOf={["Newspaper_Reader", "Newspaper_Browse"]}><Reader /></RequirePage>} />

          {/* Crawler Ops */}
          <Route path="/crawler-ops" element={<Navigate to="/crawler-ops/publishers" replace />} />
          <Route path="/crawler-ops/publishers" element={<RequirePage anyOf={CRAWLER_PAGES}><CrawlerPublishers /></RequirePage>} />
          <Route path="/crawler-ops/channels" element={<RequirePage anyOf={CRAWLER_PAGES}><CrawlerChannelMaster /></RequirePage>} />
          <Route path="/crawler-ops/sessions" element={<RequirePage anyOf={CRAWLER_PAGES}><CrawlerSessions /></RequirePage>} />
          <Route path="/crawler-ops/coverage" element={<RequirePage anyOf={CRAWLER_PAGES}><CrawlerCoverage /></RequirePage>} />
          {/* Operator agent — SuperAdmin only (backend enforces require_super_admin too) */}
          <Route path="/agent" element={<RequireGroup groups={["SuperAdmin"]}><AgentConsole harness="v1" /></RequireGroup>} />
          {/* Agent v2 — same tools and model, the researched loop: progress-based stall
              detection + recovery, mid-task steering, ask-the-operator. Runs alongside v1
              so the two can be compared on the same task. */}
          <Route path="/agent2" element={<RequireGroup groups={["SuperAdmin"]}><AgentConsole harness="v2" /></RequireGroup>} />
          {/* Agent 3 — the v2 loop plus a LIVE shared browser (np92 capture Chrome over noVNC),
              screenshots with mark-up, multi-image messages, session capture from the page.
              docs/plans/2026-10-28-agent3-live-browser.md */}
          <Route path="/agent3" element={<RequireGroup groups={["SuperAdmin"]}><AgentConsole3 /></RequireGroup>} />
          {/* Print search — full-text search over OCR'd articles (ES). SuperAdmin only;
              the API enforces require_super_admin too. */}
          <Route path="/print-search" element={<RequireGroup groups={["SuperAdmin", "Admin"]}><PrintSearch /></RequireGroup>} />
          <Route path="/page-resolution" element={<RequireGroup groups={["SuperAdmin", "Admin"]}><PageResolution /></RequireGroup>} />
          <Route path="/masters" element={<RequirePage anyOf={MASTERS_PAGES}><MastersEditions /></RequirePage>} />
          <Route path="/masters/editions" element={<RequirePage anyOf={MASTERS_PAGES}><MastersEditions /></RequirePage>} />
          <Route path="/users" element={<RequirePage anyOf={USERS_PAGES}><Users /></RequirePage>} />
          <Route path="/users/sessions" element={<RequirePage anyOf={["Newspaper_Users_Sessions"]}><UserSessions /></RequirePage>} />
          <Route path="/clips" element={
            <RequirePage anyOf={["Newspaper_ClipWorkspace", "Newspaper_ClipReview"]}>
              <Stub
                title="Clip Workspace"
                pitch="Coming soon."
                features={[
                  "Crop overlay on the page reader, drag to select rectangle.",
                  "Tag with Client, Company, Brand, Spokesperson, Industry.",
                  "Auto-compute SQCM from crop area + page physical dimensions.",
                  "Saves to crawler.PaperClipLive for downstream analytics.",
                  "Admin review queue for approving worker clips.",
                ]}
              />
            </RequirePage>
          } />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}
